define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Layer/ModelLayer',
    '../Layer/MeshLayer',
    '../Layer/ImageLayer',
    '../Layer/ElevationLayer',
], function (
    defaultValue,
    defined,
    encode,
    ModelLayer,
    MeshLayer,
    ImageLayer,
    ElevationLayer
) {
    'use strict'

    /**
     * CooServer服务连接器类，用于连接和解析服务专题树。
     * @alias ServerConnector
     * @constructor
     * @abstract
     */
    class ServerConnector {
        /**
         * @description 服务地址，仅需包含协议、地址、端口。
         * @type {String}
         */
        url = '';

        /**
         * @description token
         * @type {String}
         */
        token = '';

        /**
         * @description 是否开启调试输出信息。
         * @type {Boolean}
         */
        bDebug = false;

        /**
         * @description 创建CooServer服务连接器
         * @param {Object} props 包含如下属性的Object：
         * @param {String} props.url 服务地址，仅需包含协议、地址、端口。
         * @param {String} props.token token。
         * @param {Boolean} props.bDebug 是否开启调试输出信息。
         */
        constructor(props) {
            if (!defined(props) || !defined(props.url) || !defined(props.token)) {
                throw new Error('ServerConnector requires a url and token');
            }

            this.url = props.url;
            this.token = props.token;
            this.bDebug = defaultValue(props.bDebug, false);
        }

        /**
         * @description 获取服务信息并构建树形结构
         * @returns {Promise<Object>} 返回树形结构的Promise
         * @example 
         * let serverConnector = new CooWasm.ServerConnector({
         *     url: urlInput.value.trim(),
         *     token: tokenInput.value.trim()
         *  });
         * 
         *  let treeInfo = await serverConnector.getServiceTree();
         */
        async getServiceTree() {
            // 开始计时
            const startTime = performance.now();
            
            try {
                if (this.bDebug) console.log('开始获取服务树...');
                
                // 获取服务信息计时
                const fetchStartTime = performance.now();
                const serviceInfo = await this._fetchServiceInfo();
                const fetchEndTime = performance.now();
                if (this.bDebug) console.log(`获取服务信息耗时: ${(fetchEndTime - fetchStartTime).toFixed(2)}ms`);
                
                // 构建树形结构计时
                const buildStartTime = performance.now();
                if (this.bDebug) console.log('开始构建树形结构...');
                const treeData = this._buildTree(serviceInfo.data[0].list);
                const buildEndTime = performance.now();
                if (this.bDebug) console.log(`构建树形结构耗时: ${(buildEndTime - buildStartTime).toFixed(2)}ms`);
                if (this.bDebug) console.log('构建的树形结构:', treeData);
                
                // 获取图层信息计时
                const layerInfoStartTime = performance.now();
                if (this.bDebug) console.log('开始获取图层信息...');
                await this._populateLayerInfo(treeData);
                const layerInfoEndTime = performance.now();
                if (this.bDebug) console.log(`获取图层信息耗时: ${(layerInfoEndTime - layerInfoStartTime).toFixed(2)}ms`);
                
                // 总计时
                const endTime = performance.now();
                const totalTime = endTime - startTime;
                if (this.bDebug) console.log(`获取服务树总耗时: ${totalTime.toFixed(2)}ms`);
                if (this.bDebug) console.log('最终的树形结构:', treeData);
                
                return treeData;
            } catch (error) {
                // 即使出错也要记录总耗时
                const endTime = performance.now();
                const totalTime = endTime - startTime;
                if (this.bDebug) console.error(`获取服务树失败，耗时: ${totalTime.toFixed(2)}ms，错误:`, error);
                throw new Error(`Failed to get service tree: ${error.message}`);
            }
        }

        /**
         * @description 获取指定layerId的图层信息
         * @param {String} layerId 图层ID
         * @returns {Promise<Object>} 返回图层信息的Promise
         * @example
         * // 获取指定图层的详细信息
         * let layerInfo = await serverConnector.getLayerInfo('layer_123');
         * console.log(layerInfo.data.list); // 图层数据列表
         */
        async getLayerInfo(layerId) {
            try {
                if (this.bDebug) console.log('正在请求图层信息...', `${this.url}/api/layer/v1.0/layer_info?resource_id=${layerId}`);
                
                const response = await fetch(`${this.url}/api/layer/v1.0/layer_info?resource_id=${layerId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': 'application/json'
                    },
                    mode: 'cors' // 明确设置CORS模式
                });

                if (this.bDebug) console.log('图层信息响应状态:', response.status, response.statusText);

                if (!response.ok) {
                    const errorText = await response.text();
                    if (this.bDebug) console.error('图层信息响应错误:', errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}. Response: ${errorText}`);
                }

                const data = await response.json();
                if (this.bDebug) console.log(`图层 ${layerId} 的信息:`, data);
                return data;
            } catch (error) {
                if (this.bDebug) console.error(`获取图层信息失败 (layerId: ${layerId}):`, error);
                throw new Error(`Failed to fetch layer info: ${error.message}`);
            }
        }

        /**
         * @description 加载所有叶子节点数据，根据type转换为相应的图层对象数组
         * @param {Array} tree 服务树数据
         * @returns {Object} 包含各种图层类型数组的对象
         * @example
         * // 加载服务树中的所有图层
         * let treeData = await serverConnector.getServiceTree();
         * let layers = serverConnector.load(treeData);
         * console.log(layers.ModelLayer); // 模型图层数组
         * console.log(layers.ImageLayer); // 影像图层数组
         */
        load(tree) {
            const result = {
                ModelLayer: [],
                MeshLayer: [],
                ImageLayer: [],
                ElevationLayer: []
            };

            this._processNodes(tree, result);
            return result;
        }

        /**
         * @description 销毁所有图层对象
         * @param {Object} layers 包含各种图层类型数组的对象
         * @returns {Object} 销毁结果统计
         * @example
         * // 销毁所有已加载的图层
         * let result = serverConnector.destroy(loadedLayers);
         * console.log(result.message); // "成功销毁 5 个图层"
         * console.log(result.stats); // {ModelLayer: 2, ImageLayer: 3, ...}
         */
        destroy(layers) {
            if (!layers || typeof layers !== 'object') {
                if (this.bDebug) console.warn('没有图层数据需要销毁');
                return {
                    success: false,
                    message: '没有图层数据需要销毁',
                    destroyedCount: 0
                };
            }

            if (this.bDebug) console.log('开始销毁图层...');
            
            let destroyedCount = 0;
            const stats = {
                ModelLayer: 0,
                MeshLayer: 0,
                ImageLayer: 0,
                ElevationLayer: 0
            };

            // 销毁各类型图层
            const layerTypes = ['ModelLayer', 'MeshLayer', 'ImageLayer', 'ElevationLayer'];
            
            layerTypes.forEach(layerType => {
                const layerArray = layers[layerType];
                if (layerArray && Array.isArray(layerArray) && layerArray.length > 0) {
                    if (this.bDebug) console.log(`销毁 ${layerType} 图层，共 ${layerArray.length} 个`);
                    
                    layerArray.forEach(layer => {
                        try {
                            // 调用图层的 destroy 方法
                            if (layer && typeof layer.destroy === 'function') {
                                layer.destroy();
                                destroyedCount++;
                                stats[layerType]++;
                                if (this.bDebug) console.log(`已销毁图层: ${layer.url || 'unknown'}`);
                            } else {
                                if (this.bDebug) console.warn(`图层对象不支持destroy方法: ${layer?.url || 'unknown'}`);
                            }
                        } catch (error) {
                            if (this.bDebug) console.error(`销毁图层失败: ${layer?.url || 'unknown'}`, error);
                        }
                    });
                }
            });

            const result = {
                success: true,
                message: `成功销毁 ${destroyedCount} 个图层`,
                destroyedCount: destroyedCount,
                stats: stats
            };

            if (this.bDebug) console.log('图层销毁完成:', result);
            return result;
        }

        /**
         * @description 根据图层ID进行定位
         * @param {String} layerId 图层ID
         * @param {Object} [layers] 可选的图层数据对象，如果不提供则使用全局loadedLayers
         * @returns {Object} 定位结果
         * @example
         * // 定位到指定图层
         * let result = serverConnector.locate('layer_123');
         * if (result.success) {
         *     console.log('定位成功:', result.message);
         * } else {
         *     console.log('定位失败:', result.message);
         * }
         */
        locate(layerId, layers = null) {
            if (this.bDebug) console.log('开始定位图层, layerId:', layerId);

            if (!layerId) {
                const errorMsg = '图层ID不能为空';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            // 确定要搜索的图层数据
            const targetLayers = layers || (typeof window !== 'undefined' ? window.loadedLayers : null);
            
            if (!targetLayers) {
                const errorMsg = '没有可用的图层数据进行定位';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            try {
                // 在所有类型的图层中查找匹配的layerId
                let targetLayer = null;
                let layerType = '';

                const layerTypes = ['ModelLayer', 'MeshLayer', 'ImageLayer', 'ElevationLayer'];
                
                if (this.bDebug) {
                    console.log('查找图层对象，目标layerId:', layerId);
                    console.log('可用的图层数据:', targetLayers);
                }
                
                for (const type of layerTypes) {
                    const layerArray = targetLayers[type];
                    if (layerArray && Array.isArray(layerArray)) {
                        if (this.bDebug) console.log(`检查 ${type} 图层，共 ${layerArray.length} 个`);
                        
                        const found = layerArray.find(layer => {
                            if (this.bDebug) {
                                console.log(`检查图层: layerId=${layer.layerId}, id=${layer.id}, name=${layer.name}, url=${layer.url}`);
                            }
                            return layer.layerId === layerId || 
                                   layer.id === layerId ||
                                   (layer.name && layer.name.includes(layerId));
                        });
                        
                        if (found) {
                            targetLayer = found;
                            layerType = type;
                            if (this.bDebug) console.log(`找到匹配的图层:`, found);
                            break;
                        }
                    }
                }

                if (!targetLayer) {
                    const errorMsg = `未找到对应的图层对象 (layerId: ${layerId})`;
                    if (this.bDebug) console.warn(errorMsg);
                    return {
                        success: false,
                        message: errorMsg
                    };
                }

                // 检查图层是否支持locate方法
                if (typeof targetLayer.locate === 'function') {
                    if (this.bDebug) console.log(`调用 ${layerType} 的 locate 方法进行定位`);
                    
                    // 调用图层的locate方法
                    targetLayer.locate();
                    
                    const successMsg = `成功定位到图层: ${targetLayer.name || targetLayer.url || layerId} (${layerType})`;
                    if (this.bDebug) console.log(successMsg);
                    
                    return {
                        success: true,
                        message: successMsg,
                        layerId: layerId,
                        layerType: layerType,
                        layer: targetLayer
                    };
                } else {
                    const errorMsg = `图层不支持定位功能 (layerId: ${layerId}, type: ${layerType})`;
                    if (this.bDebug) console.warn(errorMsg);
                    return {
                        success: false,
                        message: errorMsg,
                        layerId: layerId,
                        layerType: layerType
                    };
                }

            } catch (error) {
                const errorMsg = `定位过程中发生错误: ${error.message}`;
                if (this.bDebug) console.error(errorMsg, error);
                return {
                    success: false,
                    message: errorMsg,
                    error: error
                };
            }
        }

        /**
         * @description 根据图层ID销毁指定图层
         * @param {String} layerId 图层ID
         * @param {Object} [layers] 可选的图层数据对象，如果不提供则使用全局loadedLayers
         * @returns {Object} 销毁结果
         * @example
         * // 销毁指定的图层
         * let result = serverConnector.destroyLayerById('layer_123');
         * if (result.success) {
         *     console.log('销毁成功:', result.message);
         *     console.log('已销毁图层:', result.destroyedLayer.name);
         * }
         */
        destroyLayerById(layerId, layers = null) {
            if (this.bDebug) console.log('开始销毁指定图层, layerId:', layerId);

            if (!layerId) {
                const errorMsg = '图层ID不能为空';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            // 确定要搜索的图层数据
            const targetLayers = layers || (typeof window !== 'undefined' ? window.loadedLayers : null);
            
            if (!targetLayers) {
                const errorMsg = '没有可用的图层数据进行销毁';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            try {
                // 在所有类型的图层中查找匹配的layerId
                let targetLayer = null;
                let layerType = '';
                let layerIndex = -1;

                const layerTypes = ['ModelLayer', 'MeshLayer', 'ImageLayer', 'ElevationLayer'];
                
                if (this.bDebug) {
                    console.log('查找要销毁的图层对象，目标layerId:', layerId);
                }
                
                for (const type of layerTypes) {
                    const layerArray = targetLayers[type];
                    if (layerArray && Array.isArray(layerArray)) {
                        const foundIndex = layerArray.findIndex(layer => {
                            return layer.layerId === layerId || 
                                   layer.id === layerId ||
                                   (layer.name && layer.name.includes(layerId));
                        });
                        
                        if (foundIndex !== -1) {
                            targetLayer = layerArray[foundIndex];
                            layerType = type;
                            layerIndex = foundIndex;
                            if (this.bDebug) console.log(`找到要销毁的图层:`, targetLayer);
                            break;
                        }
                    }
                }

                if (!targetLayer) {
                    const errorMsg = `未找到对应的图层对象 (layerId: ${layerId})`;
                    if (this.bDebug) console.warn(errorMsg);
                    return {
                        success: false,
                        message: errorMsg
                    };
                }

                // 销毁图层
                try {
                    if (typeof targetLayer.destroy === 'function') {
                        targetLayer.destroy();
                        
                        // 从数组中移除已销毁的图层
                        targetLayers[layerType].splice(layerIndex, 1);
                        
                        const successMsg = `成功销毁图层: ${targetLayer.name || targetLayer.url || layerId} (${layerType})`;
                        if (this.bDebug) console.log(successMsg);
                        
                        return {
                            success: true,
                            message: successMsg,
                            layerId: layerId,
                            layerType: layerType,
                            destroyedLayer: {
                                name: targetLayer.name,
                                url: targetLayer.url
                            }
                        };
                    } else {
                        const errorMsg = `图层不支持销毁功能 (layerId: ${layerId}, type: ${layerType})`;
                        if (this.bDebug) console.warn(errorMsg);
                        return {
                            success: false,
                            message: errorMsg,
                            layerId: layerId,
                            layerType: layerType
                        };
                    }
                } catch (destroyError) {
                    const errorMsg = `销毁图层时发生错误: ${destroyError.message}`;
                    if (this.bDebug) console.error(errorMsg, destroyError);
                    return {
                        success: false,
                        message: errorMsg,
                        error: destroyError
                    };
                }

            } catch (error) {
                const errorMsg = `销毁过程中发生错误: ${error.message}`;
                if (this.bDebug) console.error(errorMsg, error);
                return {
                    success: false,
                    message: errorMsg,
                    error: error
                };
            }
        }

        /**
         * @description 根据图层ID控制图层显示/隐藏
         * @param {String} layerId 图层ID
         * @param {Boolean} visible 是否显示，true为显示，false为隐藏
         * @param {Object} [layers] 可选的图层数据对象，如果不提供则使用全局loadedLayers
         * @returns {Object} 操作结果
         * @example
         * // 显示指定图层
         * let result = serverConnector.setLayerVisibility('layer_123', true);
         * if (result.success) {
         *     console.log('图层已显示:', result.message);
         * }
         * 
         * // 隐藏指定图层
         * serverConnector.setLayerVisibility('layer_123', false);
         */
        setLayerVisibility(layerId, visible, layers = null) {
            if (this.bDebug) console.log('开始设置图层显隐, layerId:', layerId, 'visible:', visible);

            if (!layerId) {
                const errorMsg = '图层ID不能为空';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            if (typeof visible !== 'boolean') {
                const errorMsg = 'visible参数必须是布尔值';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            // 确定要搜索的图层数据
            const targetLayers = layers || (typeof window !== 'undefined' ? window.loadedLayers : null);
            
            if (!targetLayers) {
                const errorMsg = '没有可用的图层数据进行显隐控制';
                if (this.bDebug) console.error(errorMsg);
                return {
                    success: false,
                    message: errorMsg
                };
            }

            try {
                // 在所有类型的图层中查找匹配的layerId
                let targetLayer = null;
                let layerType = '';

                const layerTypes = ['ModelLayer', 'MeshLayer', 'ImageLayer', 'ElevationLayer'];
                
                if (this.bDebug) {
                    console.log('查找要设置显隐的图层对象，目标layerId:', layerId);
                }
                
                for (const type of layerTypes) {
                    const layerArray = targetLayers[type];
                    if (layerArray && Array.isArray(layerArray)) {
                        const found = layerArray.find(layer => {
                            return layer.layerId === layerId || 
                                   layer.id === layerId ||
                                   (layer.name && layer.name.includes(layerId));
                        });
                        
                        if (found) {
                            targetLayer = found;
                            layerType = type;
                            if (this.bDebug) console.log(`找到要设置显隐的图层:`, found);
                            break;
                        }
                    }
                }

                if (!targetLayer) {
                    const errorMsg = `未找到对应的图层对象 (layerId: ${layerId})`;
                    if (this.bDebug) console.warn(errorMsg);
                    return {
                        success: false,
                        message: errorMsg
                    };
                }

                // 设置图层显隐
                try {
                    // 检查图层是否有update方法（优先使用）或bVisible属性
                    if (typeof targetLayer.update === 'function') {
                        targetLayer.update({bVisible: visible});
                        
                        const actionText = visible ? '显示' : '隐藏';
                        const successMsg = `成功${actionText}图层: ${targetLayer.name || targetLayer.url || layerId} (${layerType})`;
                        if (this.bDebug) console.log(successMsg);
                        
                        return {
                            success: true,
                            message: successMsg,
                            layerId: layerId,
                            layerType: layerType,
                            visible: visible,
                            layer: {
                                name: targetLayer.name,
                                url: targetLayer.url,
                                bVisible: visible
                            }
                        };
                    } else if ('bVisible' in targetLayer) {
                        targetLayer.bVisible = visible;
                        
                        const actionText = visible ? '显示' : '隐藏';
                        const successMsg = `成功${actionText}图层: ${targetLayer.name || targetLayer.url || layerId} (${layerType})`;
                        if (this.bDebug) console.log(successMsg);
                        
                        return {
                            success: true,
                            message: successMsg,
                            layerId: layerId,
                            layerType: layerType,
                            visible: visible,
                            layer: {
                                name: targetLayer.name,
                                url: targetLayer.url,
                                bVisible: targetLayer.bVisible
                            }
                        };
                    } else if (typeof targetLayer.setVisible === 'function') {
                        targetLayer.setVisible(visible);
                        
                        const actionText = visible ? '显示' : '隐藏';
                        const successMsg = `成功${actionText}图层: ${targetLayer.name || targetLayer.url || layerId} (${layerType})`;
                        if (this.bDebug) console.log(successMsg);
                        
                        return {
                            success: true,
                            message: successMsg,
                            layerId: layerId,
                            layerType: layerType,
                            visible: visible,
                            layer: {
                                name: targetLayer.name,
                                url: targetLayer.url
                            }
                        };
                    } else {
                        const errorMsg = `图层不支持显隐控制功能 (layerId: ${layerId}, type: ${layerType})`;
                        if (this.bDebug) console.warn(errorMsg);
                        return {
                            success: false,
                            message: errorMsg,
                            layerId: layerId,
                            layerType: layerType
                        };
                    }
                } catch (visibilityError) {
                    const errorMsg = `设置图层显隐时发生错误: ${visibilityError.message}`;
                    if (this.bDebug) console.error(errorMsg, visibilityError);
                    return {
                        success: false,
                        message: errorMsg,
                        error: visibilityError
                    };
                }

            } catch (error) {
                const errorMsg = `显隐控制过程中发生错误: ${error.message}`;
                if (this.bDebug) console.error(errorMsg, error);
                return {
                    success: false,
                    message: errorMsg,
                    error: error
                };
            }
        }

        /**
         * @description 为树节点填充图层信息
         * @private
         * @param {Array} nodes 树节点数组
         * @returns {Promise<void>}
         */
        async _populateLayerInfo(nodes) {
            if (!Array.isArray(nodes)) {
                return;
            }

            for (const node of nodes) {
                if (!node.isParent && node.layerId && node.layerId !== '-1') {
                    try {
                        const layerInfo = await this.getLayerInfo(node.layerId);
                        if (layerInfo && layerInfo.data && Array.isArray(layerInfo.data.list)) {
                            node.data = layerInfo.data.list.map(item => {
                                // 创建新对象，排除不需要的属性
                                const { id, bizKey, createTime, ...filteredItem } = item;
                                return filteredItem;
                            });
                        }
                    } catch (error) {
                        if (this.bDebug) console.warn(`Failed to fetch layer info for layerId ${node.layerId}:`, error.message);
                    }
                }

                if (node.children && node.children.length > 0) {
                    await this._populateLayerInfo(node.children);
                }
            }
        }

        /**
         * @description 获取服务信息
         * @private
         * @returns {Promise<Object>} 返回服务信息的Promise
         */
        async _fetchServiceInfo() {
            try {
                if (this.bDebug) console.log('正在请求服务信息...', `${this.url}/api/layer/v1.0/service_info`);
                
                const response = await fetch(`${this.url}/api/layer/v1.0/service_info`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': 'application/json'
                    },
                    mode: 'cors' // 明确设置CORS模式
                });

                if (this.bDebug) console.log('响应状态:', response.status, response.statusText);

                if (!response.ok) {
                    const errorText = await response.text();
                    if (this.bDebug) console.error('响应错误内容:', errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}. Response: ${errorText}`);
                }

                const data = await response.json();
                if (this.bDebug) console.log('获取到的服务信息:', data);
                return data;
            } catch (error) {
                if (this.bDebug) console.error('获取服务信息失败:', error);
                throw new Error(`Failed to fetch service info: ${error.message}`);
            }
        }

        /**
         * @description 将平级结构转换为树形结构
         * @private
         * @param {Array} list 平级数据列表
         * @returns {Array} 树形结构数组
         */
        _buildTree(list) {
            if (!Array.isArray(list)) {
                return [];
            }

            const nodeMap = new Map();
            const roots = [];

            list.forEach(item => {
                const node = this._createTreeNode(item);
                nodeMap.set(item.cid, node);
            });

            list.forEach(item => {
                const node = nodeMap.get(item.cid);
                
                if (item.pid === '0' && item.cid === '-1') {
                    roots.push(node);
                } else if (item.pid !== '0') {
                    const parent = nodeMap.get(item.pid);
                    if (parent) {
                        if (!parent.children) {
                            parent.children = [];
                        }
                        parent.children.push(node);
                    } else {
                        roots.push(node);
                    }
                } else {
                    roots.push(node);
                }
            });

            return roots;
        }

        /**
         * @description 创建树节点
         * @private
         * @param {Object} item 原始数据项
         * @returns {Object} 树节点对象
         */
        _createTreeNode(item) {
            // 将服务类型转换为对应的图层类型
            let mappedType = 'GROUPNODE';
            const serviceType = item.serviceType || '';
            
            if (serviceType.startsWith('T0')) {
                mappedType = 'ModelLayer'; // T0** 也映射为 ModelLayer
            } else {
                switch (serviceType) {
                    case 'GMS':
                        mappedType = 'ModelLayer';
                        break;
                    case 'OSGB':
                        mappedType = 'MeshLayer';
                        break;
                    case 'DOM':
                        mappedType = 'ImageLayer';
                        break;
                    case 'DEM':
                        mappedType = 'ElevationLayer';
                        break;
                    default:
                        mappedType = serviceType || 'GROUPNODE';
                }
            }
            
            return {
                layerId: item.layerId,
                name: item.name,
                isParent: item.isParent === '1',
                isExpanded: item.openOrClose === '1',
                visible: parseInt(item.visible) || 0,
                type: mappedType,
                data: []
            };
        }

        

        /**
         * @description 递归处理节点，提取叶子节点数据
         * @private
         * @param {Array} nodes 节点数组
         * @param {Object} result 结果对象
         */
        _processNodes(nodes, result) {
            if (!Array.isArray(nodes)) {
                return;
            }

            nodes.forEach(node => {
                if (!node.isParent && node.data && node.data.length > 0) {
                    // 处理叶子节点的数据，传递节点的visible信息和layerId
                    node.data.forEach(dataItem => {
                        const layerObject = this._createLayerObject(node.type, dataItem, node.visible, node.layerId);
                        if (layerObject && result[node.type]) {
                            result[node.type].push(layerObject);
                        }
                    });
                }

                // 递归处理子节点
                if (node.children && node.children.length > 0) {
                    this._processNodes(node.children, result);
                }
            });
        }

        /**
         * @description 根据图层类型和数据创建相应的图层对象
         * @private
         * @param {String} layerType 图层类型
         * @param {Object} dataItem 数据项
         * @param {Number} nodeVisible 节点的visible值，控制图层可见性
         * @param {String} layerId 图层ID
         * @returns {Object|null} 图层对象或null
         */
        _createLayerObject(layerType, dataItem, nodeVisible, layerId) {
            // 检查数据格式支持性
            if (!this._isDataTypeSupported(layerType, dataItem.dataType)) {
                if (this.bDebug) console.warn(`Unsupported data type ${dataItem.dataType} for ${layerType}`);
                return null;
            }

            switch (layerType) {
                case 'ModelLayer':
                    return this._createModelLayer(dataItem, nodeVisible, layerId);
                case 'MeshLayer':
                    return this._createMeshLayer(dataItem, nodeVisible, layerId);
                case 'ImageLayer':
                    return this._createImageLayer(dataItem, nodeVisible, layerId);
                case 'ElevationLayer':
                    return this._createElevationLayer(dataItem, nodeVisible, layerId);
                default:
                    if (this.bDebug) console.warn(`Unknown layer type: ${layerType}`);
                    return null;
            }
        }

        /**
         * @description 检查数据类型是否支持
         * @private
         * @param {String} layerType 图层类型
         * @param {String} dataType 数据类型
         * @returns {Boolean} 是否支持
         */
        _isDataTypeSupported(layerType, dataType) {
            // 所有图层类型都不支持 sqlite 格式
            if (dataType === 'sqlite') {
                return false;
            }
            // normal 格式都支持
            if (dataType === 'normal') {
                return true;
            }
            // 其他格式默认支持（可根据需要扩展）
            return true;
        }

        /**
         * @description 转换内部fullPath为外部访问地址
         * @private
         * @param {String} fullPath 原始的fullPath地址
         * @returns {String} 转换后的外部访问地址
         */
        _convertToExternalUrl(fullPath) {
            if (!fullPath) {
                return '';
            }

            try {
                // 解析原始URL
                const originalUrl = new URL(fullPath);
                
                // 解析当前访问的URL
                const currentUrl = new URL(this.url);
                
                // 构建新的URL：使用当前访问的协议、主机和端口，保留原始的路径
                const newUrl = new URL(originalUrl.pathname + originalUrl.search + originalUrl.hash, currentUrl.origin);
                
                if (this.bDebug) console.log(`URL转换: ${fullPath} -> ${newUrl.toString()}`);
                return newUrl.toString();
            } catch (error) {
                if (this.bDebug) console.warn(`URL转换失败: ${fullPath}`, error);
                // 如果转换失败，返回原始地址
                return fullPath;
            }
        }

        /**
         * @description 创建ModelLayer对象
         * @private
         * @param {Object} dataItem 数据项
         * @param {Number} nodeVisible 节点的visible值
         * @param {String} layerId 图层ID
         * @returns {Object} ModelLayer对象
         */
        _createModelLayer(dataItem, nodeVisible, layerId) {
            // 转换内部地址为外部访问地址
            const externalUrl = this._convertToExternalUrl(dataItem.fullPath || dataItem.url || '');
            
            let modelLayer = new ModelLayer({
                url: externalUrl,
                bCompress: dataItem.compressed === true || dataItem.compressed === 'true',
                bVisible: nodeVisible === 1 // 将节点的visible信息传递给图层对象
            })
            
            // 添加layerId属性用于定位查找
            modelLayer.layerId = layerId;
            modelLayer.name = dataItem.fileName || dataItem.name || '';
            
            return modelLayer
        }

        /**
         * @description 创建MeshLayer对象
         * @private
         * @param {Object} dataItem 数据项
         * @param {Number} nodeVisible 节点的visible值
         * @param {String} layerId 图层ID
         * @returns {Object} MeshLayer对象
         */
        _createMeshLayer(dataItem, nodeVisible, layerId) {
            // 转换内部地址为外部访问地址
            const externalUrl = this._convertToExternalUrl(dataItem.fullPath || dataItem.url || '');
            
            let meshLayer = new MeshLayer({
                url: externalUrl,
                bVisible: nodeVisible === 1 // 将节点的visible信息传递给图层对象
            })
            
            // 添加layerId属性用于定位查找
            meshLayer.layerId = layerId;
            meshLayer.name = dataItem.fileName || dataItem.name || '';
            
            return meshLayer
        }

        /**
         * @description 创建ImageLayer对象
         * @private
         * @param {Object} dataItem 数据项
         * @param {Number} nodeVisible 节点的visible值
         * @param {String} layerId 图层ID
         * @returns {Object} ImageLayer对象
         */
        _createImageLayer(dataItem, nodeVisible, layerId) {
            // 转换内部地址为外部访问地址
            const externalUrl = this._convertToExternalUrl(dataItem.fullPath || dataItem.url || '');
            
            let imageLayer = new ImageLayer({
                url: externalUrl,
                bVisible: nodeVisible === 1 // 将节点的visible信息传递给图层对象
            })
            
            // 添加layerId属性用于定位查找
            imageLayer.layerId = layerId;
            imageLayer.name = dataItem.fileName || dataItem.name || '';
            
            return imageLayer
        }

        /**
         * @description 创建ElevationLayer对象
         * @private
         * @param {Object} dataItem 数据项
         * @param {Number} nodeVisible 节点的visible值
         * @param {String} layerId 图层ID
         * @returns {Object} ElevationLayer对象
         */
        _createElevationLayer(dataItem, nodeVisible, layerId) {
            // 转换内部地址为外部访问地址
            const externalUrl = this._convertToExternalUrl(dataItem.fullPath || dataItem.url || '');
            
            let elevationLayer = new ElevationLayer({
                url: externalUrl,
                bVisible: nodeVisible === 1 // 将节点的visible信息传递给图层对象
            })
            
            // 添加layerId属性用于定位查找
            elevationLayer.layerId = layerId;
            elevationLayer.name = dataItem.fileName || dataItem.name || '';
            
            return elevationLayer
        }
    }

    return ServerConnector;
});